import { geodesicDistance } from '../domain/measurement';

/** ≈ GLO-30 sample spacing at CASUR latitudes (1″ ≈ 30.3 m E-W, 30.9 m N-S at 11.5° N). */
export const PROFILE_SPACING_M = 30;
/** Upper bound on samples so a very long line cannot freeze the UI; spacing grows instead (reported to the user). */
export const MAX_PROFILE_SAMPLES = 4_000;
/** «Cotas cada» presets (m). Below ~30 m the values are interpolated from the same 1″ grid: no extra resolution. */
export const PROFILE_SPACING_OPTIONS = [10, 20, 30, 50, 100] as const;
export const MIN_PROFILE_SPACING_M = 5;
export const MAX_PROFILE_SPACING_M = 1_000;

/** A custom «Cotas cada» distance: a finite number of metres within [5, 1 000]; anything else is rejected (null). */
export function parseProfileSpacing(input: unknown): number | null {
  const text = typeof input === 'number' ? String(input) : typeof input === 'string' ? input.trim().replace(',', '.') : '';
  if (!/^\d+(\.\d+)?$/.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) && value >= MIN_PROFILE_SPACING_M && value <= MAX_PROFILE_SPACING_M ? value : null;
}

export interface RouteSample {
  /** Cumulative geodesic distance from the start (m). */
  readonly distance: number;
  /** Map coordinate (EPSG:3857). */
  readonly coordinate: readonly [number, number];
  /** True for the line's own vertices (always sampled). */
  readonly vertex: boolean;
}

export interface SampledRoute {
  readonly samples: readonly RouteSample[];
  readonly length: number;
  /** Largest gap actually used between consecutive samples (m). */
  readonly spacing: number;
  /** The interval that was asked for (m). */
  readonly requestedSpacing: number;
  /** True when MAX_PROFILE_SAMPLES forced a step larger than the requested one. */
  readonly limited: boolean;
  /**
   * Set when the line itself has more vertices than MAX_PROFILE_SAMPLES: the profile then reads a temporary,
   * evenly spread subset of its vertices (always the first and last). The source geometry is never changed.
   */
  readonly vertexCount: number;
  readonly decimated: boolean;
}

const lerp = (a: readonly number[], b: readonly number[], t: number): [number, number] => [a[0]! + (b[0]! - a[0]!) * t, a[1]! + (b[1]! - a[1]!) * t];

/** More vertices than the cap: keep the first, the last and the vertex nearest to evenly spaced distances. */
function decimatedVertices(vertices: readonly (readonly number[])[], segmentLengths: readonly number[], spacing: number): SampledRoute {
  const cumulative = [0];
  segmentLengths.forEach((length, index) => cumulative.push(cumulative[index]! + length));
  const total = cumulative[cumulative.length - 1]!;
  const picked: number[] = [0];
  let cursor = 0;
  for (let k = 1; k < MAX_PROFILE_SAMPLES; k += 1) {
    const target = total * k / (MAX_PROFILE_SAMPLES - 1);
    while (cursor < cumulative.length - 1 && Math.abs(cumulative[cursor + 1]! - target) <= Math.abs(cumulative[cursor]! - target)) cursor += 1;
    if (picked[picked.length - 1] !== cursor) picked.push(cursor);
  }
  if (picked[picked.length - 1] !== vertices.length - 1) picked[picked.length === MAX_PROFILE_SAMPLES ? picked.length - 1 : picked.length] = vertices.length - 1;
  let largest = 0;
  const samples: RouteSample[] = picked.map((index, position) => {
    if (position) largest = Math.max(largest, cumulative[index]! - cumulative[picked[position - 1]!]!);
    return { distance: cumulative[index]!, coordinate: [vertices[index]![0]!, vertices[index]![1]!], vertex: true };
  });
  return { samples, length: total, spacing: largest, requestedSpacing: spacing, limited: true, vertexCount: vertices.length, decimated: true };
}

/**
 * Samples a line every ≤ `spacing` metres of geodesic distance, always including every vertex: `spacing` is the
 * MAXIMUM gap between readings, and vertices closer together than that produce shorter steps. The total number of
 * samples never exceeds MAX_PROFILE_SAMPLES (vertices beyond the cap are thinned, see `decimatedVertices`). Intermediate points
 * lie on the drawn segment (straight in the map projection, i.e. what the user sees); distances are cumulative
 * geodesic distances between consecutive samples (the same measure used by «Medir distancia»).
 */
export function sampleRoute(coordinates: readonly (readonly number[])[], spacing = PROFILE_SPACING_M): SampledRoute {
  const vertices = coordinates.filter((coordinate, index) => index === 0 || coordinate[0] !== coordinates[index - 1]![0] || coordinate[1] !== coordinates[index - 1]![1]);
  if (!(spacing > 0) || !Number.isFinite(spacing)) throw new Error('El intervalo de muestreo debe ser un número positivo.');
  if (vertices.length < 2) return { samples: vertices.length ? [{ distance: 0, coordinate: [vertices[0]![0]!, vertices[0]![1]!], vertex: true }] : [], length: 0, spacing: 0, requestedSpacing: spacing, limited: false, vertexCount: vertices.length, decimated: false };
  const segmentLengths = vertices.slice(1).map((vertex, index) => geodesicDistance(vertices[index]!, vertex));
  if (vertices.length > MAX_PROFILE_SAMPLES) return decimatedVertices(vertices, segmentLengths, spacing);
  const total = segmentLengths.reduce((sum, value) => sum + value, 0);
  // Vertices are always kept; the intermediate budget is what remains under the cap.
  const effective = Math.max(spacing, total / Math.max(1, MAX_PROFILE_SAMPLES - vertices.length));
  const samples: RouteSample[] = [{ distance: 0, coordinate: [vertices[0]![0]!, vertices[0]![1]!], vertex: true }];
  let distance = 0; let largest = 0;
  vertices.slice(1).forEach((end, index) => {
    const start = vertices[index]!;
    const parts = Math.max(1, Math.ceil(segmentLengths[index]! / effective - 1e-9));
    let previous: readonly number[] = start;
    for (let part = 1; part <= parts; part += 1) {
      const coordinate = part === parts ? [end[0]!, end[1]!] as [number, number] : lerp(start, end, part / parts);
      const step = geodesicDistance(previous, coordinate);
      distance += step; largest = Math.max(largest, step);
      samples.push({ distance, coordinate, vertex: part === parts });
      previous = coordinate;
    }
  });
  return { samples, length: distance, spacing: largest, requestedSpacing: spacing, limited: effective > spacing * (1 + 1e-9), vertexCount: vertices.length, decimated: false };
}
